import React from 'react';
import { api, SecurityReport } from '../api';
import { Section } from './primitives';
import { OperationFailure, OperationResult, useOperationRun } from './operation/OperationResult';
import { actionLabel } from './operation/actionLabels';
import { SshStat, SshStats } from './SshStats';
import { StatusBadge } from './ui';

interface SecuritySweepCardProps {
  environmentId: string;
}

/** What the sweep looks at, in the words of the card's description. */
const covered = 'exposed configuration files, the admin path, file permissions and developer mode';

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** The verdict of a finished sweep, from the counts the server reports. */
function verdictOf(report: SecurityReport): { tone: 'success' | 'warning' | 'danger'; verdict: string } {
  const exposed = report.exposed_files.length;
  if (report.critical_count > 0 || exposed > 0) {
    const parts = [
      report.critical_count > 0 ? plural(report.critical_count, 'critical finding', 'critical findings') : '',
      exposed > 0 ? plural(exposed, 'exposed file', 'exposed files') : '',
    ].filter(Boolean);
    return { tone: 'danger', verdict: `${parts.join(' and ')} found` };
  }
  if (report.findings.length > 0) return { tone: 'warning', verdict: `${plural(report.findings.length, 'finding', 'findings')} to review, none critical` };
  return { tone: 'success', verdict: 'No critical problems or exposed files found' };
}

export const SecuritySweepCard: React.FC<SecuritySweepCardProps> = ({ environmentId }) => {
  const { running, result, failure, run } = useOperationRun(() => api.getSecuritySweep(environmentId), 'The server gave no reason.');
  const report = result?.value ?? null;
  // A 200 that says it did not succeed is a failed sweep, not an empty one.
  const incomplete = report && !report.succeeded ? report : null;
  const answer = report && report.succeeded ? verdictOf(report) : null;

  return (
    <Section
      title="Security sweep"
      testId="security-sweep"
      actions={
        <button type="button" onClick={run} disabled={running} className="button button--secondary">
          {running ? 'Scanning' : failure ? actionLabel.retry : report ? 'Scan again' : 'Scan store security'}
        </button>
      }
    >
      {failure ? (
        <OperationFailure
          verdict="The security sweep did not finish"
          at={failure.at}
          scope="Nothing was changed"
          reason={failure.message}
          testId="security-sweep-failure"
        />
      ) : null}

      {incomplete && result ? (
        <OperationFailure
          verdict="The security sweep did not finish"
          at={result.at}
          scope="Incomplete: score and findings are not shown"
          output={incomplete.error_output}
          testId="security-sweep-incomplete"
        />
      ) : null}

      {report && answer && result ? (
        <OperationResult
          tone={answer.tone}
          verdict={answer.verdict}
          at={result.at}
          scope={`Covers ${covered}`}
          testId="security-sweep-result"
        >
          <SshStats>
            <SshStat
              label="Rating"
              value={<StatusBadge kind="securityRating" value={report.overall_rating} />}
              detail={`Score ${report.security_score}/100`}
            />
            <SshStat
              label="Admin path"
              value={<span className="font-mono">/{report.admin_path}</span>}
              detail={report.custom_admin_path ? 'Custom path' : 'Default path: change it'}
            />
            <SshStat
              label="Exposed files"
              value={
                <StatusBadge
                  kind="auditResult"
                  value={report.exposed_files.length === 0 ? 'passed' : 'failed'}
                  label={report.exposed_files.length === 0 ? 'None' : `${report.exposed_files.length} exposed`}
                />
              }
            />
            <SshStat
              label="Findings"
              value={`${report.critical_count} critical`}
              detail={`${report.high_count} high, ${report.medium_count} medium`}
            />
          </SshStats>

          {report.findings.length > 0 ? (
            <ul className="status-list status-list--stacked" aria-label="Security findings">
              {report.findings.map((f, i) => (
                <li key={`${f.title}-${i}`} className="status-list__item">
                  <span className="status-list__name">{f.title}</span>
                  <StatusBadge kind="severity" value={f.severity} />
                  <div className="status-list__detail">
                    {f.description}
                    <div>
                      Fix: <span className="font-mono">{f.remediation}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : null}
        </OperationResult>
      ) : null}

      {!report && !failure ? <p className="quiet-empty">Not scanned yet</p> : null}
    </Section>
  );
};
